import { useEffect } from 'react';
import { BetSlip } from './BetSlip';
import { useBetSlip } from './betSlipContext';
import { formatOdds } from './multi';

/*
  The slip below `xl`, where AppShell has no right rail.

  Two pieces, both driven by the shared slip context so they cannot disagree
  with the desktop rail about what is held:

    • A floating pill ("1 selection · Sydney FC @ 2.29") pinned above the
      bottom nav whenever a selection is held and the sheet is closed. It is
      the answer to "I tapped odds and nothing happened": something visibly
      arrives, where the thumb already is, and tapping it opens the sheet.
    • The sheet itself — a bottom-anchored panel with a backdrop. `pick` opens
      it straight away (the prototype's behaviour), placing closes it, and the
      handle, the backdrop and Escape close it without clearing the selection.

  Both are `xl:hidden`: at `xl` the rail renders the same slip and this file
  contributes nothing to the layout. Not a portal, same as Modal — the shell
  is the only stacking context that matters here.
*/
export function BetSlipSheet() {
  const { legs, isMulti, combinedOdds, sheetOpen, openSheet, closeSheet } = useBetSlip();

  useEffect(() => {
    if (!sheetOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeSheet();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheetOpen, closeSheet]);

  if (!legs.length) return null;
  const first = legs[0]!;

  if (!sheetOpen) {
    return (
      <button
        type="button"
        onClick={openSheet}
        className="xl:hidden fixed left-1/2 -translate-x-1/2 bottom-[76px] z-40 flex items-center gap-2.5 rounded-full border border-brand-teal/40 bg-brand-navy2/95 backdrop-blur-md pl-3 pr-4 py-2.5 shadow-glow text-sm text-white hover:border-brand-teal transition max-w-[calc(100vw-32px)]"
        aria-label="Open bet slip"
      >
        <span className="w-6 h-6 rounded-full grid place-items-center bg-brand-teal text-[#052e35] text-[11px] font-extrabold">{legs.length}</span>
        <span className="truncate">
          <span className="font-semibold">{isMulti ? `${legs.length}-leg multi` : first.selection.label}</span>
          <span className="text-white/60"> @ {formatOdds(combinedOdds)}</span>
        </span>
        <span className="text-brand-teal font-semibold shrink-0">Bet slip ›</span>
      </button>
    );
  }

  return (
    <div className="xl:hidden fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={closeSheet} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Bet slip"
        className="relative max-h-[92vh] overflow-y-auto rounded-t-3xl border-t border-brand-teal/30 bg-brand-surface px-5 pt-3 pb-[max(20px,env(safe-area-inset-bottom))] shadow-glow"
      >
        <button type="button" onClick={closeSheet} className="mx-auto mb-3 block h-1.5 w-10 rounded-full bg-white/20" aria-label="Close bet slip" />
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-[17px]">{isMulti ? 'Place a multi' : 'Place a bet'}</h3>
          <button
            type="button"
            onClick={closeSheet}
            className="w-8 h-8 rounded-lg grid place-items-center text-white/50 hover:text-white hover:bg-white/[0.06] transition"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>
          </button>
        </div>
        <BetSlip variant="sheet" />
      </div>
    </div>
  );
}
